import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapboxToken } from '@/hooks/useMapboxToken';
import { Loader2, MapPinOff, LocateFixed } from 'lucide-react';

export type MapItem = {
  id: string;
  lat: number;
  lng: number;
  title: string;
  subtitle?: string;
  priceLabel: string;
  image?: string;
  link: string;
};

interface Props {
  items: MapItem[];
  isLoading?: boolean;
  /** Render a single sidebar card. Receives the original item plus hover handlers. */
  renderCard?: (
    itemId: string,
    helpers: { isActive: boolean; onMouseEnter: () => void; onMouseLeave: () => void },
  ) => React.ReactNode;
  /** Legacy fallback when renderCard is not provided. */
  sidebar?: React.ReactNode;
  emptyText?: string;
  /** Visual style for unclustered points. 'pin-label' shows a blue house pin + name label. */
  markerVariant?: 'circle' | 'pin-label';
}

const DEFAULT_CENTER: [number, number] = [38.7578, 9.0192];
const DEFAULT_ZOOM = 11;

const PRIMARY = 'hsl(180, 45%, 45%)';
const ACCENT = 'hsl(330, 55%, 75%)';
const ACCENT_DARK = 'hsl(330, 50%, 55%)';
const HIGHLIGHT = 'hsl(215, 65%, 38%)';

export function ListingsMapView({ items, isLoading, renderCard, sidebar, emptyText = 'No items to display.', markerVariant = 'circle' }: Props) {
  const navigate = useNavigate();
  const { data: token, isLoading: tokenLoading, error: tokenError } = useMapboxToken();
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const popupRef = useRef<mapboxgl.Popup | null>(null);
  const [bounds, setBounds] = useState<mapboxgl.LngLatBounds | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const geo = useMemo(
    () =>
      items
        .map((i) => ({ ...i, lat: Number(i.lat), lng: Number(i.lng) }))
        .filter((i) => Number.isFinite(i.lat) && Number.isFinite(i.lng) && !(i.lat === 0 && i.lng === 0)),
    [items],
  );

  // Items whose coords fall inside the current map viewport
  const visibleGeo = useMemo(() => {
    if (!bounds) return geo;
    return geo.filter((i) => bounds.contains([i.lng, i.lat]));
  }, [geo, bounds]);

  // init map
  useEffect(() => {
    if (!token || !containerRef.current || mapRef.current) return;
    if (!token.startsWith('pk.')) return;
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
    });
    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.once('load', () => {
      map.resize();
      setBounds(map.getBounds());
    });
    const sync = () => setBounds(map.getBounds());
    map.on('moveend', sync);
    map.on('zoomend', sync);
    mapRef.current = map;
    return () => {
      map.off('moveend', sync);
      map.off('zoomend', sync);
      map.remove();
      mapRef.current = null;
    };
  }, [token]);

  const fitToItems = useCallback((map: mapboxgl.Map) => {
    if (!geo.length) return;
    if (geo.length === 1) {
      map.flyTo({ center: [geo[0].lng, geo[0].lat], zoom: 14, duration: 800, essential: true });
      return;
    }
    const b = new mapboxgl.LngLatBounds();
    geo.forEach((i) => b.extend([i.lng, i.lat]));
    if (!b.isEmpty()) map.fitBounds(b, { padding: 60, maxZoom: 14, duration: 800 });
  }, [geo]);

  // load / update data (circle/cluster variant)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerVariant !== 'circle') return;


    const apply = () => {
      const fc = {
        type: 'FeatureCollection' as const,
        features: geo.map((i) => ({
          type: 'Feature' as const,
          properties: {
            id: i.id,
            title: i.title,
            subtitle: i.subtitle || '',
            priceLabel: i.priceLabel,
            image: i.image || '',
            link: i.link,
          },
          geometry: { type: 'Point' as const, coordinates: [i.lng, i.lat] },
        })),
      };

      const existing = map.getSource('items') as mapboxgl.GeoJSONSource | undefined;
      if (existing) {
        existing.setData(fc as any);
      } else {
        map.addSource('items', {
          type: 'geojson',
          data: fc as any,
          cluster: true,
          clusterMaxZoom: 14,
          clusterRadius: 50,
          generateId: true,
        });

        map.addLayer({
          id: 'clusters',
          type: 'circle',
          source: 'items',
          filter: ['has', 'point_count'],
          paint: {
            'circle-color': PRIMARY,
            'circle-radius': ['step', ['get', 'point_count'], 18, 10, 22, 50, 28, 200, 34],
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.95,
          },
        });
        map.addLayer({
          id: 'cluster-count',
          type: 'symbol',
          source: 'items',
          filter: ['has', 'point_count'],
          layout: {
            'text-field': ['get', 'point_count_abbreviated'],
            'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
            'text-size': 13,
            'text-allow-overlap': true,
          },
          paint: { 'text-color': '#ffffff' },
        });

        map.addLayer({
          id: 'unclustered',
          type: 'circle',
          source: 'items',
          filter: ['!', ['has', 'point_count']],
          paint: {
            'circle-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              HIGHLIGHT,
              ACCENT,
            ],
            'circle-stroke-color': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              '#ffffff',
              ACCENT_DARK,
            ],
            'circle-stroke-width': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              3,
              2,
            ],
            'circle-radius': [
              'case',
              ['boolean', ['feature-state', 'hover'], false],
              13,
              9,
            ],
          },
        });

        map.on('click', 'clusters', (e) => {
          const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
          const clusterId = features[0].properties?.cluster_id;
          (map.getSource('items') as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
            clusterId,
            (err, zoom) => {
              if (err) return;
              const coords = (features[0].geometry as any).coordinates;
              map.easeTo({ center: coords, zoom });
            },
          );
        });

        map.on('click', 'unclustered', (e) => {
          const f = e.features?.[0];
          if (!f) return;
          const p: any = f.properties || {};
          if (p.link) navigate(p.link);
        });

        ['clusters', 'unclustered'].forEach((layer) => {
          map.on('mouseenter', layer, () => (map.getCanvas().style.cursor = 'pointer'));
          map.on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''));
        });
        // Bi-directional hover: map circle → card
        map.on('mousemove', 'unclustered', (e) => {
          const f = e.features?.[0];
          const id = (f?.properties as any)?.id;
          if (id) {
            hoverSourceRef.current = 'map';
            setHoveredId(id);
          }
        });
        map.on('mouseleave', 'unclustered', () => {
          hoverSourceRef.current = 'map';
          setHoveredId(null);
        });
      }

      if (geo.length) {
        map.resize();
        fitToItems(map);
        setBounds(map.getBounds());
      }
    };

    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [geo, markerVariant]);

  // HTML markers (pin-label variant) — blue house pin + project name label
  const htmlMarkersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const hoverSourceRef = useRef<'map' | 'card' | null>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerVariant !== 'pin-label') return;

    const buildEl = (item: typeof geo[number]) => {
      // Outer wrapper — Mapbox sets transform on this; do NOT set transform here.
      const wrapper = document.createElement('div');
      wrapper.className = 'pin-label-marker';
      wrapper.style.cssText = 'cursor:pointer;';
      // Inner element handles our visual offset/scale so we don't fight Mapbox's positioning.
      wrapper.innerHTML = `
        <div class="pin-inner" style="
          display:flex;flex-direction:column;align-items:center;
          transform:translateY(-4px);transition:transform .15s ease;will-change:transform;
        ">
          <div class="pin-shape" style="
            position:relative;width:38px;height:46px;
            filter:drop-shadow(0 3px 4px rgba(0,0,0,.35));
          ">
            <svg viewBox="0 0 38 46" width="38" height="46" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 0C8.5 0 0 8.4 0 18.8c0 13.8 19 27.2 19 27.2s19-13.4 19-27.2C38 8.4 29.5 0 19 0z" fill="hsl(215, 75%, 45%)"/>
              <circle cx="19" cy="19" r="13" fill="#ffffff"/>
              <path d="M19 11.5l-7 5.5v7.5h4.2v-4.5h5.6v4.5H26V17z" fill="hsl(215, 75%, 45%)"/>
            </svg>
          </div>
          <div class="pin-label" style="
            margin-top:-3px;max-width:160px;padding:3px 8px;border-radius:6px;
            background:hsl(215, 75%, 45%);color:#fff;font-size:11px;font-weight:600;
            white-space:nowrap;overflow:hidden;text-overflow:ellipsis;
            box-shadow:0 2px 4px rgba(0,0,0,.25);
            border:1px solid rgba(255,255,255,.4);
          ">${item.title.replace(/</g, '&lt;')}</div>
        </div>
      `;
      wrapper.addEventListener('mouseenter', () => {
        hoverSourceRef.current = 'map';
        setHoveredId(item.id);
      });
      wrapper.addEventListener('mouseleave', () => {
        hoverSourceRef.current = 'map';
        setHoveredId(null);
      });
      wrapper.addEventListener('click', () => {
        popupRef.current?.remove();
        if (item.link) navigate(item.link);
      });
      return wrapper;
    };

    const sync = () => {
      const existing = htmlMarkersRef.current;
      const nextIds = new Set(geo.map((g) => g.id));
      // remove stale
      existing.forEach((m, id) => {
        if (!nextIds.has(id)) { m.remove(); existing.delete(id); }
      });
      // add/update
      geo.forEach((item) => {
        const prev = existing.get(item.id);
        if (prev) {
          prev.setLngLat([item.lng, item.lat]);
        } else {
          const marker = new mapboxgl.Marker({ element: buildEl(item), anchor: 'bottom' })
            .setLngLat([item.lng, item.lat])
            .addTo(map);
          existing.set(item.id, marker);
        }
      });
      if (geo.length) {
        map.resize();
        fitToItems(map);
        setBounds(map.getBounds());
      }
    };

    if (map.isStyleLoaded()) sync();
    else map.once('load', sync);

    return () => {
      // cleanup on unmount only — variant switches handled above
    };
  }, [geo, markerVariant, fitToItems]);

  // Clean up all HTML markers on unmount
  useEffect(() => {
    return () => {
      htmlMarkersRef.current.forEach((m) => m.remove());
      htmlMarkersRef.current.clear();
    };
  }, []);


  // Apply hover feature-state when hoveredId changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerVariant === 'circle' && map.getSource('items')) {
      const features = map.querySourceFeatures('items');
      features.forEach((f) => {
        if (f.id == null) return;
        const isHovered = (f.properties as any)?.id === hoveredId;
        map.setFeatureState({ source: 'items', id: f.id }, { hover: isHovered });
      });
    } else if (markerVariant === 'pin-label') {
      htmlMarkersRef.current.forEach((marker, id) => {
        const el = marker.getElement();
        const inner = el.querySelector('.pin-inner') as HTMLElement | null;
        const label = el.querySelector('.pin-label') as HTMLElement | null;
        const pin = el.querySelector('.pin-shape') as HTMLElement | null;
        const active = id === hoveredId;
        // Mapbox owns el.style.transform — only mutate the inner wrapper.
        if (inner) inner.style.transform = active ? 'translateY(-8px) scale(1.08)' : 'translateY(-4px)';
        el.style.zIndex = active ? '20' : '';
        if (label) label.style.background = active ? 'hsl(330, 55%, 50%)' : 'hsl(215, 75%, 45%)';
        if (pin) pin.style.filter = active ? 'drop-shadow(0 5px 8px rgba(0,0,0,.45))' : 'drop-shadow(0 3px 4px rgba(0,0,0,.35))';
      });
    }
  }, [hoveredId, markerVariant]);


  const handleReFit = () => {
    const map = mapRef.current;
    if (map) fitToItems(map);
  };

  // When hover originates on the map, scroll the matching sidebar card into view
  useEffect(() => {
    if (!hoveredId || hoverSourceRef.current !== 'map') return;
    const el = cardRefs.current.get(hoveredId);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [hoveredId]);

  // Decide which ids appear in the sidebar list
  const sidebarIds = useMemo(() => new Set(visibleGeo.map((g) => g.id)), [visibleGeo]);
  const sidebarItems = useMemo(
    () => items.filter((i) => sidebarIds.has(i.id)),
    [items, sidebarIds],
  );

  // Flyto + popup when hovering a card
  const handleCardHover = useCallback((id: string | null) => {
    hoverSourceRef.current = 'card';
    setHoveredId(id);
    const map = mapRef.current;
    if (!map || !id) {
      if (!id) popupRef.current?.remove();
      return;
    }
    const item = geo.find((g) => g.id === id);
    if (!item) return;
    popupRef.current?.remove();
    const html = `
      <div style="width:220px; font-family:inherit">
        ${item.image ? `<img src="${item.image}" alt="" style="width:100%; height:100px; object-fit:cover; border-radius:6px"/>` : ''}
        <div style="padding:6px 4px 2px">
          <div style="font-size:13px; font-weight:700; line-height:1.3">${item.title || ''}</div>
          <div style="font-size:12px; font-weight:700; color:hsl(215,65%,38%); margin-top:2px">${item.priceLabel || ''}</div>
        </div>
      </div>`;
    popupRef.current = new mapboxgl.Popup({ offset: 14, maxWidth: '240px', closeButton: false, closeOnClick: false })
      .setLngLat([item.lng, item.lat])
      .setHTML(html)
      .addTo(map);
  }, [geo]);

  if (tokenError) {
    const message = tokenError instanceof Error ? tokenError.message : 'Failed to load map.';
    return (
      <div className="h-[calc(100vh-220px)] flex items-center justify-center bg-muted/30 rounded-lg border">
        <p className="max-w-md px-4 text-center text-sm text-muted-foreground">{message}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(420px,46%)_1fr] gap-3 h-[calc(100vh-220px)] min-h-[640px]">
      <div className="overflow-y-auto pr-1 space-y-2 hidden lg:block">
        {geo.length > 0 && (
          <div className="flex items-center justify-between px-1 pt-1 sticky top-0 bg-background z-10 pb-1">
            <span className="text-xs text-muted-foreground">
              {visibleGeo.length} of {geo.length} in map view
            </span>
            <button
              onClick={handleReFit}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm"
              title="Re-fit map to all listings"
            >
              <LocateFixed className="h-3.5 w-3.5" />
              Re-fit map
            </button>
          </div>
        )}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!isLoading && items.length === 0 && (
          <div className="text-center py-12 text-sm text-muted-foreground">{emptyText}</div>
        )}
        {!isLoading && geo.length === 0 && items.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex gap-2">
            <MapPinOff className="h-4 w-4 shrink-0" />
            <span>None of the matching items have coordinates yet. They appear in List view.</span>
          </div>
        )}
        {!isLoading && geo.length > 0 && visibleGeo.length === 0 && (
          <div className="text-center py-8 text-xs text-muted-foreground border rounded-md">
            No listings in the current map view. Zoom out or move the map.
          </div>
        )}
        {renderCard
          ? sidebarItems.map((it) => (
              <div
                key={it.id}
                ref={(el) => {
                  if (el) cardRefs.current.set(it.id, el);
                  else cardRefs.current.delete(it.id);
                }}
              >
                {renderCard(it.id, {
                  isActive: hoveredId === it.id,
                  onMouseEnter: () => handleCardHover(it.id),
                  onMouseLeave: () => handleCardHover(null),
                })}
              </div>
            ))
          : sidebar}
      </div>
      <div className="relative rounded-lg overflow-hidden border bg-muted/20 h-full">
        {(tokenLoading || !token) && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/40 z-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <div ref={containerRef} className="absolute inset-0" />
      </div>
    </div>
  );
}
